import { useState, useEffect } from 'react'
import { type MRT_PaginationState } from 'material-react-table'
import * as Sentry from '@sentry/react'
import { getPendingSubmissions, searchPendingSubmissions } from '../../../pocketbase/PocketbasePendingSubmissionsService'
import { usePbMetadataStore } from '../../../stores/pbMetadataStore'
import { pb } from '../../../pocketbase/Pocketbase'
import type { IBrand, INote, IPerfumer, ICountry, IPrice } from '../../../pocketbase/PocketbaseMetadataService'
import { SubmissionsTable } from './SubmissionsTable'
import { useNavigate } from 'react-router-dom'
import { ApproveSubmissionDialog } from '../../../components/dialogs/ApproveSubmissionDialog'
import { DenySubmissionDialog } from '../../../components/dialogs/DenySubmissionDialog'

// Extended interface for pending submissions with metadata
export interface IPendingSubmissionWithMetadata {
  id: string
  name: string
  imgUrl: string | null
  description: string
  year: number | null

  // Brand - can be relation or text
  brand: IBrand | undefined
  brand_text: string | undefined

  // Country - can be relation or text
  country: ICountry | undefined
  country_text: string | undefined

  // Perfumers - can be relations, text arrays, or both
  perfumers: IPerfumer[]
  perfumers_text: string[]

  // Price - always a relation
  price: IPrice | undefined

  // Notes - can be relations, text arrays, or both
  topNotes: INote[]
  topNotes_text: string[]
  middleNotes: INote[]
  middleNotes_text: string[]
  bottomNotes: INote[]
  bottomNotes_text: string[]

  // Denial fields
  is_denied: boolean
  denied_reason: string

  // Submission metadata
  submitted_by: string
  created: string
}

export const SubmissionsView = () => {
  const navigate = useNavigate()
  const [submissions, setSubmissions] = useState<any[]>([])
  const [submissionsWithMetadata, setSubmissionsWithMetadata] = useState<IPendingSubmissionWithMetadata[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const [rowCount, setRowCount] = useState(0)
  const [searchValue, setSearchValue] = useState('')

  // Confirmation dialog state
  const [approveDialogOpen, setApproveDialogOpen] = useState(false)
  const [pendingApproveId, setPendingApproveId] = useState<string | null>(null)

  // Deny dialog state
  const [denyDialogOpen, setDenyDialogOpen] = useState(false)
  const [pendingDenyId, setPendingDenyId] = useState<string | null>(null)

  const { getBrandById, getNoteById, getPerfumerById, getCountryById, getPriceById, fetchAllMetadata, isLoaded: isMetadataLoaded } = usePbMetadataStore()

  // Fetch metadata on mount
  useEffect(() => {
    fetchAllMetadata()
  }, [])

  // Reset to first page when search changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }))
  }, [searchValue])

  // Load submissions when pagination or search changes
  useEffect(() => {
    loadSubmissions()
  }, [pagination.pageIndex, pagination.pageSize, searchValue])

  // Convert submissions to metadata format whenever submissions or metadata changes
  useEffect(() => {
    const withMetadata = submissions.map(submission => {
      return convertToMetadata(submission)
    }).filter(Boolean) as IPendingSubmissionWithMetadata[]

    setSubmissionsWithMetadata(withMetadata)
  }, [submissions, isMetadataLoaded])

  const convertToMetadata = (submission: any): IPendingSubmissionWithMetadata => {
    // Get image URL if image exists
    let imgUrl: string | null = null
    if (submission.image) {
      imgUrl = pb.files.getURL(submission, submission.image)
    }

    // Parse JSON arrays for custom notes
    // Pocketbase returns JSON fields as already-parsed arrays, not strings
    const parseJsonArray = (value: string[] | string | null | undefined): string[] => {
      if (!value) return []

      // If it's already an array, return it
      if (Array.isArray(value)) {
        return value
      }

      // If it's a string, try to parse it
      try {
        const parsed = JSON.parse(value)
        return Array.isArray(parsed) ? parsed : []
      } catch (e) {
        console.error('Failed to parse JSON array', e)
        Sentry.captureException(e)
        return []
      }
    }

    return {
      id: submission.id,
      name: submission.name,
      imgUrl,
      description: submission.description || '',
      year: submission.year || null,

      // Brand - check if relation exists, otherwise use text
      brand: submission.brand ? getBrandById(submission.brand) : undefined,
      brand_text: submission.brand_text || undefined,

      // Country - check if relation exists, otherwise use text
      country: submission.country ? getCountryById(submission.country) : undefined,
      country_text: submission.country_text || undefined,

      // Perfumers - combine relations and text
      perfumers: submission.perfumers
        ? (Array.isArray(submission.perfumers)
          ? submission.perfumers.map((id: string) => getPerfumerById(id)).filter(Boolean) as IPerfumer[]
          : [])
        : [],
      perfumers_text: parseJsonArray(submission.perfumers_text),

      // Price - always a relation
      price: submission.price ? getPriceById(submission.price) : undefined,

      // Top Notes - combine relations and text
      topNotes: submission.top_notes
        ? (Array.isArray(submission.top_notes)
          ? submission.top_notes.map((id: string) => getNoteById(id)).filter(Boolean) as INote[]
          : [])
        : [],
      topNotes_text: parseJsonArray(submission.top_notes_text),

      // Middle Notes - combine relations and text
      middleNotes: submission.middle_notes
        ? (Array.isArray(submission.middle_notes)
          ? submission.middle_notes.map((id: string) => getNoteById(id)).filter(Boolean) as INote[]
          : [])
        : [],
      middleNotes_text: parseJsonArray(submission.middle_notes_text),

      // Bottom Notes - combine relations and text
      bottomNotes: submission.bottom_notes
        ? (Array.isArray(submission.bottom_notes)
          ? submission.bottom_notes.map((id: string) => getNoteById(id)).filter(Boolean) as INote[]
          : [])
        : [],
      bottomNotes_text: parseJsonArray(submission.bottom_notes_text),

      // Denial fields
      is_denied: submission.is_denied || false,
      denied_reason: submission.denied_reason || '',

      // Metadata
      submitted_by: submission.submitted_by,
      created: submission.created,
    }
  }

  const loadSubmissions = async () => {
    try {
      setIsLoading(true)

      // Pocketbase uses 1-indexed pages
      const page = pagination.pageIndex + 1
      const search = searchValue ? searchValue.trim() : undefined

      const result = search
        ? await searchPendingSubmissions(page, pagination.pageSize, search)
        : await getPendingSubmissions(page, pagination.pageSize)

      setSubmissions(result.items)
      setRowCount(result.totalItems)
    } catch (error) {
      console.error('Error loading submissions:', error)
      Sentry.captureException(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleEdit = (submission: IPendingSubmissionWithMetadata) => {
    // Navigate to SubmitPage2 in edit mode with the submission ID
    navigate(`/submit?id=${submission.id}`)
  }

  const handleApprove = (submissionId: string) => {
    setPendingApproveId(submissionId)
    setApproveDialogOpen(true)
  }

  const handleApproveSuccess = async () => {
    // Reload submissions to reflect the changes
    await loadSubmissions()
    setPendingApproveId(null)
  }

  const handleDeny = (submissionId: string) => {
    setPendingDenyId(submissionId)
    setDenyDialogOpen(true)
  }

  const handleDenySuccess = async () => {
    // Reload submissions to reflect the changes
    await loadSubmissions()
    setPendingDenyId(null)
  }

  return (
    <div className={'flex flex-col h-full'}>
      {/* Fixed Header */}
      <div
        className={
          'flex items-center justify-between px-8 pb-6 pt-8 border-b border-searchBorder'
        }
      >
        <h1 className={'text-2xl font-bold'}>
          Pending Submissions
        </h1>
      </div>

      {/* Material React Table */}
      <div className={'flex-1 overflow-hidden'}>
        <SubmissionsTable
          data={submissionsWithMetadata}
          isLoading={isLoading}
          pagination={pagination}
          rowCount={rowCount}
          onPaginationChange={setPagination}
          onEdit={handleEdit}
          onApprove={handleApprove}
          onDeny={handleDeny}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
        />
      </div>

      {/* Approve Submission Dialog */}
      <ApproveSubmissionDialog
        isOpen={approveDialogOpen}
        onClose={() => {
          setApproveDialogOpen(false)
          setPendingApproveId(null)
        }}
        onSuccess={handleApproveSuccess}
        submissionId={pendingApproveId}
      />

      {/* Deny Submission Dialog */}
      <DenySubmissionDialog
        isOpen={denyDialogOpen}
        onClose={() => {
          setDenyDialogOpen(false)
          setPendingDenyId(null)
        }}
        onSuccess={handleDenySuccess}
        submissionId={pendingDenyId}
      />
    </div>
  )
}
